import axios from "axios";
import { fidexaHubApi } from "@/api/fidexaHub";
import type {
  FidexaHubNotification,
  FidexaHubNotificationPromptAnswer,
  FidexaHubNotifications,
  FidexaHubNotificationSettings,
  FidexaHubPushSubscriptionPayload,
} from "./fidexaHubNotificationType";

const extractApiErrorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    const message = error.response?.data?.message;
    if (Array.isArray(message) && message.length > 0) return message[0];
    if (typeof message === "string" && message) return message;
  }
  return fallback;
};

const mapNotification = (raw: Record<string, unknown>): FidexaHubNotification => ({
  id: raw["id"] as string,
  chatId: raw["chat_id"] as string,
  title: raw["title"] as string,
  body: raw["body"] as string,
  messageCount: (raw["message_count"] as number | undefined) ?? 1,
  createdAt: raw["created_at"] as string,
  updatedAt: raw["updated_at"] as string,
  readAt: (raw["read_at"] as string | null) ?? null,
});

const list = async (): Promise<FidexaHubNotifications> => {
  try {
    const { data } = await fidexaHubApi.get("v2/fidexa-hub/notifications");
    return {
      items: ((data["items"] as Record<string, unknown>[]) ?? []).map(mapNotification),
      unreadCount: (data["unread_count"] as number | undefined) ?? 0,
    };
  } catch (error) {
    throw new Error(extractApiErrorMessage(error, "Não foi possível carregar as notificações"));
  }
};

const markRead = async (notificationId: string): Promise<void> => {
  await fidexaHubApi.post(`v2/fidexa-hub/notifications/${notificationId}/read`);
};

const markAllRead = async (): Promise<void> => {
  try {
    await fidexaHubApi.post("v2/fidexa-hub/notifications/read");
  } catch (error) {
    throw new Error(extractApiErrorMessage(error, "Não foi possível marcar as notificações como lidas"));
  }
};

const getSettings = async (): Promise<FidexaHubNotificationSettings> => {
  const { data } = await fidexaHubApi.get("v2/fidexa-hub/notifications/settings");
  return {
    promptAnswer: (data["prompt_answer"] as FidexaHubNotificationPromptAnswer | null) ?? null,
    pushPublicKey: (data["push_public_key"] as string | null) ?? null,
  };
};

const answerPrompt = async (answer: FidexaHubNotificationPromptAnswer): Promise<void> => {
  await fidexaHubApi.put("v2/fidexa-hub/notifications/settings/prompt", { answer });
};

const saveSubscription = async (payload: FidexaHubPushSubscriptionPayload): Promise<void> => {
  try {
    await fidexaHubApi.post("v2/fidexa-hub/notifications/push-subscriptions", {
      endpoint: payload.endpoint,
      p256dh: payload.p256dh,
      auth: payload.auth,
      platform: payload.platform,
      user_agent: payload.userAgent,
    });
  } catch (error) {
    throw new Error(extractApiErrorMessage(error, "Não foi possível ativar as notificações"));
  }
};

// `sessionToken` explícito: no logout o token já saiu do storage quando esta
// chamada sai, e o interceptor não teria o que mandar.
const revokeSubscription = async (endpoint: string, sessionToken?: string): Promise<void> => {
  await fidexaHubApi.post(
    "v2/fidexa-hub/notifications/push-subscriptions/revoke",
    { endpoint },
    sessionToken ? { headers: { Authorization: `Bearer ${sessionToken}` } } : undefined
  );
};

export const fidexaHubNotificationService = {
  list,
  markRead,
  markAllRead,
  getSettings,
  answerPrompt,
  saveSubscription,
  revokeSubscription,
};
